import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  ICarrierAccount,
  RedoCarrierName,
  redoCarrierNameToCarrier,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { t } from "@redotech/redo-i18n";
import { assertNever } from "@redotech/util/type";

/** Display labels/logos for account carrier variants; kept dependency-light so specs can import them. */
export const carrierCardLabel = (carrier: RedoCarrierName) => {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (carrier) {
    case RedoCarrierName.UPS_MAIL_INNOVATIONS: {
      return "UPS Mail Innovations®";
    }
    case RedoCarrierName.UPS_SUREPOST: {
      return "UPS Ground Saver®";
    }
    case RedoCarrierName.USPS_DIRECT: {
      return "USPS";
    }
    case RedoCarrierName.CANADA_POST: {
      return t("{{carrier}} (Deprecated)", { carrier: "Canada Post" });
    }
    case RedoCarrierName.CANADA_POST_DIRECT: {
      return "Canada Post";
    }
    case RedoCarrierName.UPS: {
      const mappedCarrier = redoCarrierNameToCarrier[carrier];
      if (!mappedCarrier) {
        return carrier;
      }
      return carrierToLabel[mappedCarrier] ?? carrier;
    }
    case RedoCarrierName.P2P_GLOBAL: {
      return "Point2Point Global";
    }
    default: {
      return carrier;
    }
  }
};

export function getCanadaPostAccountLabel(
  carrierAccount: ICarrierAccount,
): string | undefined {
  if (carrierAccount.redoCarrierName === RedoCarrierName.CANADA_POST) {
    return carrierCardLabel(RedoCarrierName.CANADA_POST);
  }

  if (carrierAccount.provider !== RateProvider.CANADA_POST) {
    return undefined;
  }

  const baseLabel = carrierCardLabel(RedoCarrierName.CANADA_POST_DIRECT);
  switch (carrierAccount.apiPlatform) {
    case "legacy":
      return `${baseLabel} · Legacy API`;
    case "developer_portal":
      return `${baseLabel} · Developer Portal API`;
    default:
      assertNever(carrierAccount.apiPlatform);
  }
}

export const isResoldCarrierAccount = (carrier: RedoCarrierName): boolean =>
  carrier === RedoCarrierName.FREIGHTWIRE;

export const carrierCardLogoCarrier = (
  carrier: RedoCarrierName,
): Carriers | string => {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (carrier) {
    case RedoCarrierName.CANADA_POST_DIRECT:
      return Carriers.CANADA_POST;
    case RedoCarrierName.USPS_DIRECT:
      return Carriers.USPS_SHIP;
    case RedoCarrierName.DPD_NL:
      return Carriers.DPD;
    case RedoCarrierName.FREIGHTWIRE:
      // Maps to FEDEX, but the card shows FreightWire's own logo.
      return RedoCarrierName.FREIGHTWIRE;
    default:
      return redoCarrierNameToCarrier[carrier] ?? carrier;
  }
};
